import DateService from '../services/date.service';

import {
  Day,
  DatesAvailabilityType,
  DateRangeMode,
  DateWorkingMode,
} from '../models/entities/calendar.model';

import DateTimeFormatOptions = Intl.DateTimeFormatOptions;

export default class CalendarService {
  static addDays(
    locale: string,
    startDate: Date,
    selectedDates: DateRangeMode,
    remainingDays: number,
    dealerCalendar: DatesAvailabilityType,
    monthShown: number
  ): Day[] {
    const sheet: Day[] = [];
    let iterator = 0;
    let otherDay: Date;
    while (iterator < remainingDays) {
      otherDay = new Date(startDate);
      otherDay.setUTCDate(startDate.getDate() + iterator);
      sheet.push(
        CalendarService.buildDay(
          locale,
          otherDay,
          selectedDates,
          dealerCalendar,
          monthShown
        )
      );
      iterator += 1;
    }
    return sheet;
  }

  static addPreviousDays(
    locale: string,
    startDate: Date,
    selectedDates: DateRangeMode,
    dealerCalendar: DatesAvailabilityType,
    weekStartsOn: string,
    monthShown: number
  ): Day[] {
    const weekday = CalendarService.getWeekDay(startDate, weekStartsOn);
    const firstDayOfWeek = CalendarService.firstDayOfWeek(
      startDate,
      weekStartsOn
    );
    const offset = 0;
    const correctedOffset = CalendarService.compensateForStartOfWeek(
      weekday,
      offset,
      weekStartsOn
    );
    return CalendarService.addDays(
      locale,
      firstDayOfWeek,
      selectedDates,
      correctedOffset,
      dealerCalendar,
      monthShown
    );
  }

  static buildDay(
    locale: string,
    value: Date,
    selectedDates: DateRangeMode,
    dealerCalendar: DatesAvailabilityType,
    monthShown: number
  ): Day {
    const selectedDatesToString = Object.values(selectedDates)?.map((sd) => {
      return DateService.formatDateForVBS(sd);
    });
    const { availableDates } = dealerCalendar;
    const { closedDates } = dealerCalendar;

    const actualDate = value && DateService.formatDateForVBS(value);
    const actualDateMonth = value.getMonth();
    const datetime = DateService.toIsoStringShort(value);
    const enabled = !!(
      availableDates &&
      availableDates.indexOf(datetime) > -1 &&
      closedDates &&
      closedDates.indexOf(datetime) === -1
    );
    const enabledNotInMonth = enabled && actualDateMonth !== monthShown;
    const disabledNotInMonth = !enabled && actualDateMonth !== monthShown;
    const selected = selectedDatesToString.includes(actualDate);
    const inBetween = DateService.inBetweenDates(value, {
      startDate: selectedDates.start,
      endDate: selectedDates.end,
    });
    const today = new Date();

    const closed = !!(
      closedDates &&
      availableDates &&
      closedDates.includes(actualDate) &&
      availableDates.indexOf(datetime) > -1 &&
      new Date(actualDate) > today
    );

    const currentDay = DateService.formatDateForVBS(today) === actualDate;
    const text = DateService.formatUTCDate(locale, value);
    return {
      enabled,
      enabledNotInMonth,
      disabledNotInMonth,
      selected,
      text,
      value,
      datetime,
      inBetween,
      closed,
      currentDay,
    };
  }

  static compensateForStartOfWeek(
    weekday: number,
    offset: number,
    weekStartsOn: string
  ): number {
    let val;
    if (weekday < offset) {
      // when start of week is in a day after start day,
      // offset to previous week, taking current day into account
      const compensate = CalendarService.week(weekStartsOn).length + 1;
      val = compensate - offset;
    } else {
      val = weekday - offset;
    }
    return val;
  }

  static firstDayOfWeek(startDate: Date, weekStartsOn: string): Date {
    const weekday = CalendarService.getWeekDay(startDate, weekStartsOn);
    const firstDayOfWeek = CalendarService.startOfWeek(
      startDate,
      weekday,
      weekStartsOn
    );
    return firstDayOfWeek;
  }

  static getCalendar(
    locale: string,
    startDate: Date,
    selectedDates: DateRangeMode,
    daysInRow: number,
    weeksInRow: number,
    rowsInSheet: number,
    dealerCalendar: DatesAvailabilityType,
    weekStartsOn: string
  ): Day[] {
    // we set a new variable for the start date in case we need to setDate() the starting date
    const pageStart = new Date(startDate);
    const monthShown = pageStart.getMonth();
    let calendar: Day[] = [];
    const previousDays = CalendarService.addPreviousDays(
      locale,
      pageStart,
      selectedDates,
      dealerCalendar,
      weekStartsOn,
      monthShown
    );
    calendar = calendar.concat(previousDays);
    const totalDays = weeksInRow * daysInRow * rowsInSheet;
    const remainingDays = totalDays - calendar.length;
    const remainingPeriod = CalendarService.addDays(
      locale,
      pageStart,
      selectedDates,
      remainingDays,
      dealerCalendar,
      monthShown
    );
    const lastDayOfLastRowDate =
      remainingPeriod[remainingPeriod.length - 1].value;
    const lastDayOfLastRow = lastDayOfLastRowDate.getDate();
    const isBetween = lastDayOfLastRow < 7;
    calendar = calendar.concat(
      isBetween ? remainingPeriod : remainingPeriod.slice(0, -7)
    );
    return calendar;
  }

  static getWeekDay(date: Date, weekStartsOn: string): number {
    const options: DateTimeFormatOptions = {
      weekday: 'short',
    };
    const weekday = new Intl.DateTimeFormat('en', options)
      .format(date)
      .toLowerCase();
    return CalendarService.week(weekStartsOn).indexOf(weekday);
  }

  static getWeekDayIndex(weekday: string, weekStartsOn: string): number {
    return CalendarService.week(weekStartsOn).indexOf(weekday);
  }

  static isStartMode(mode: DateWorkingMode): boolean {
    return mode === DateWorkingMode.START;
  }

  static startOfWeek(
    startDate: Date,
    weekday: number,
    weekStartsOn: string
  ): Date {
    const date = new Date(startDate);
    const offset =
      weekday === 0
        ? -6
        : CalendarService.getWeekDayIndex(weekStartsOn, weekStartsOn);
    const correctedOffset = CalendarService.compensateForStartOfWeek(
      weekday,
      offset,
      weekStartsOn
    );
    const diff = date.getDate() - correctedOffset;
    const dateUTC0 = new Date(date.setDate(diff));
    return DateService.buildDate(dateUTC0);
  }

  static week(weekStartsOn: string): string[] {
    const week = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    const index = week.indexOf(weekStartsOn);
    const nextWeek = week.slice(0, index);
    const thisWeek = week.splice(index);
    const sortedWeek = thisWeek.concat(nextWeek);
    return sortedWeek;
  }

  static buildGenericCalendar(initialUnavailableDays: number): {
    availableDates: string[];
    closedDates: string[];
  } {
    const firstDayOfCalendar = new Date();
    firstDayOfCalendar.setDate(
      firstDayOfCalendar.getDate() + initialUnavailableDays
    );

    const year = firstDayOfCalendar.getFullYear();
    const month = firstDayOfCalendar.getMonth();
    const lastDayOfCalendar = new Date(year, month + 4, 0);

    const currentDate: Date = firstDayOfCalendar;
    const availableDates: string[] = [];
    const closedDates: string[] = [];

    while (lastDayOfCalendar > currentDate) {
      const currentDateLabel = `${currentDate.getFullYear()}-${`0${
        currentDate.getMonth() + 1
      }`.slice(-2)}-${`0${currentDate.getDate()}`.slice(-2)}`;

      // TODO: Initial Calendar configurable per market
      // if ([0, 6].includes(currentDate.getDay())) {
      //   closedDates.push(currentDateLabel);
      // } else {
      availableDates.push(currentDateLabel);
      // }

      currentDate.setDate(currentDate.getDate() + 1);
    }

    return { availableDates, closedDates };
  }

  static buildInitialSelectedDay(initialUnavailableDays: number): {
    start: Date;
  } {
    const selectedDate = new Date();
    selectedDate.setDate(selectedDate.getDate() + initialUnavailableDays);

    if ([0, 6].includes(selectedDate.getDay())) {
      while ([0, 6].includes(selectedDate.getDay())) {
        selectedDate.setDate(selectedDate.getDate() + 1);
      }
    }

    return { start: selectedDate };
  }
}
